import type { PlayItem, Channel, UserAlert, OnDemandContent, OnDemandItem, ContentType, ContentProperties } from "@ca.vmedia.tv/domains";
import { useChannelsStore } from "@/stores/Channels";
import { AppConstants } from "@/common";
import { useUserInfoStore } from "@/stores/UserInfo";
import { Services } from "@ca.vmedia.tv/services";
import { TimeUtils, Logger } from '@/utils';
import { useRouter } from 'vue-router';
import { linkItems } from "@/components/Header/types";
import { OnDemandCardData, type CardData, ChannelLiveCardData, EpisodeCardData } from "@/components/Cards/types";
import { useApplicationStore } from "@/stores/Application";
import { useDetailShowPageStore } from "@/stores/DetailShowPage";
import { useHomePageStore } from "@/stores/HomePage";
import { useFetchRelatedContent } from '@/composables/useFetchRelatedContent';
import { useEmptyLine } from "@/composables/useEmptyLine";
import { useDetailPageStore } from "@/stores/DetailPage";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';
import { ButtonsTypes as SubtitlesButtonsTypes } from '@/components/Player/components/SubtitleModal/types/enums/ButtonsTypes';
import { Strings } from "@/locales/types/enums/strings";

export const usePlayerStore = defineStore('Player', (): ExposeInterface => {
    const { $i18n } = useNuxtApp();

    const DetailPageStore = useDetailPageStore();
    const {
        getCurrentItemDetail,
    } = storeToRefs(DetailPageStore);
    
    const { 
        emptyOnDemandContent
    } = useEmptyLine();

    const ApplicationStore = useApplicationStore();
    const {
        showPreviewSignInModal,
        setTooltipeShowing, 
        setTooltipeMessage
     } = ApplicationStore;
     const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    const router = useRouter();
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: availableChannels,
        getNotAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);
    
    const DetailShowPageStore = useDetailShowPageStore();
    const {
        getEpisodesProperties: _episodesPropertiesDetailPage,
        getEpisodes: _episodesDetailPage,
        getIsLoadingEpisodes: _isLoadingEpisodesDetailPage
    } = storeToRefs(DetailShowPageStore);
    const { fetchNextPageOfEpisodes: fetchNextPageOfEpisodesDetailShowPage } = DetailShowPageStore;

    const HomePageStore = useHomePageStore();
    const {
        getEpisodesProperties: _episodesPropertiesHomePage,
        getEpisodes: _episodesHomePage,
        getIsLoadingEpisodes: _isLoadingEpisodesHomePage
    } = storeToRefs(HomePageStore);
    const { fetchNextPageOfEpisodes: fetchNextPageOfEpisodesHomePage } = HomePageStore;

    const _VISIBLE_ITEMS_IN_LINE = 5;

    const errorChannelId = 'No current program for channel id';
    const _playProgram: Ref<PlayItem | null> = ref(null);
    const _playChannel: Ref<PlayItem | null> = ref(null);
    const _subtitles: Ref<string> = ref('');
    const _audio: Ref<string> = ref('');
    const _playerAvailability: Ref<boolean> = ref(false);
    const _isPlayerInitialized: Ref<boolean> = ref(false);
    const _isUpdateProgram: Ref<boolean> = ref(false);
    const _closePlayer: Ref<boolean> = ref(false);

    const _isPlayBeginning: Ref<boolean> = ref(false);
    const _alertsIds: Ref<number[]> = ref([]);
    const _alertsRetrieved: Ref<UserAlert[]> = ref([]);
    const _isAlertModalVisible: Ref<boolean> = ref(false);

    const _bifImage: Ref<string | null> = ref(null);

    const getIdDetailPage = computed((): number | null => getCurrentItemDetail.value ? getCurrentItemDetail.value.id : null);
    const playProgram = computed((): PlayItem | null => _playProgram.value);
    const playChannel = computed((): PlayItem | null => _playChannel.value);
    const subtitles = computed((): string => _subtitles.value);
    const audio = computed((): string => _audio.value);

    const _liveProgram: Ref<PlayItem | null> = ref(null);
    const liveProgram = computed((): PlayItem | null => _liveProgram.value);

    const playerAvailability = computed((): boolean => _playerAvailability.value);
    const isPlayerInitialized = computed((): boolean => _isPlayerInitialized.value);
    const isUpdateProgram = computed((): boolean => _isUpdateProgram.value);
    const isPlayerClosure = computed((): boolean => _closePlayer.value);

    const _playingIsChannel: Ref<boolean> = ref(false);
    const playingIsChannel = computed((): boolean => _playingIsChannel.value);
    const setPlayingIsChannel = (value: boolean): void => {
        _playingIsChannel.value = value;
    };

    const isPlayBeginning = computed((): boolean => _isPlayBeginning.value);
    const alertsIds = computed((): number[] => _alertsIds.value);
    const alertsRetrieved = computed((): UserAlert[] => _alertsRetrieved.value);
    const isAlertModalVisible = computed((): boolean => _isAlertModalVisible.value);

    const _videoResume: Ref<boolean> = ref(false);
    const videoResume = computed((): boolean => _videoResume.value);
    const setVideoResume = (value: boolean): void => {
        _videoResume.value = value;
    };

    const bifImage = computed((): string | null => _bifImage.value);

    const _currentProgram: Ref<PlayItem | null> = ref(null);
    const currentProgram = computed((): PlayItem | null => _currentProgram.value);

    const _currentChannel: Ref<Channel | null> = ref(null);
    const getCurrentChannel = computed((): Channel | null => _currentChannel.value ?? allChannels.value.find((channel: Channel) => channel.id === playChannel.value?.id) ?? null);
    const setCurrentChannel = (item: Channel): void => {
        _currentChannel.value = item;
    };

    const prevChannelsPageCounter: Ref<number> = ref(0);
    const nextChannelsPageCounter: Ref<number> = ref(0);
    const _channelsCards: Ref<Channel[]> = ref([]);
    const _startChannelIndex = computed((): number => {
        if (!getCurrentChannel.value && !currentProgram.value?.recentAirTime) {
            return 0;
        }

        const playingChannelId = getCurrentChannel.value?.id || currentProgram.value?.recentAirTime?.channelId;
        const playingChannelIndex = availableChannels.value.findIndex(item => item.id === playingChannelId);
        if (playingChannelIndex === -1) {
            return 0;
        }
        const maxStartIndex = availableChannels.value.length - _VISIBLE_ITEMS_IN_LINE;
        if (playingChannelIndex > maxStartIndex) {
            return Math.max(0, maxStartIndex);
        }

        return playingChannelIndex;
    });
    const _prevChannelIndex = computed((): number => {
        const prevIndex = _startChannelIndex.value - (prevChannelsPageCounter.value * AppConstants.PlayerItemsInLine);
        if (prevIndex < 0) {
            return 0;
        }
        return prevIndex;
    });
    const _endChannelIndex = computed((): number => {
        const endIndex = _startChannelIndex.value + ((nextChannelsPageCounter.value + 1) * AppConstants.PlayerItemsInLine);
        if (endIndex > availableChannels.value.length) {
            return availableChannels.value.length;
        }
        return endIndex;
    });

    const getChannelsCards = computed((): ChannelLiveCardData[] => {
        return _channelsCards.value.map(
            (item: Channel) => {
                return new ChannelLiveCardData(item);
            }
        );
    });

    const _isLoadingChannelCards: Ref<boolean> = ref(false);
    const getIsLoadingChannelCards: ComputedRef<boolean> = computed(() => _isLoadingChannelCards.value);
    const _setIsLoadingChannelCards = (isLoading: boolean) => {
        _isLoadingChannelCards.value = isLoading;
    };

    const initChannelCards = async (): Promise<void> => {
        _setIsLoadingChannelCards(true);
        const channels = availableChannels.value.slice(_startChannelIndex.value, _endChannelIndex.value);
        const channelIds = channels.map((channel: Channel) => channel.id).filter((id): id is number => id !== null);
        
        // Request programs for all channels.
        await Services.Instance.guide.getSegmentFromCache(channelIds, currentTime.value);

        _channelsCards.value = channels;
        _setIsLoadingChannelCards(false);
    };

    const loadNextChannelPage = async (): Promise<void> => {
        if(_endChannelIndex.value >= availableChannels.value.length) {
            return;
        }
        _setIsLoadingChannelCards(true);
        
        nextChannelsPageCounter.value += 1;
        const channels = availableChannels.value.slice(_startChannelIndex.value + ((nextChannelsPageCounter.value) * AppConstants.PlayerItemsInLine), _endChannelIndex.value);
        const channelIds = channels.map((channel: Channel) => channel.id).filter((id): id is number => id !== null);

        // Request programs for all channels.
        await Services.Instance.guide.getSegmentFromCache(channelIds, currentTime.value);

        _channelsCards.value.push(...channels);
        _setIsLoadingChannelCards(false);
    };

    const loadPreviousChannelPage = async (): Promise<void> => {
        if (_prevChannelIndex.value === 0) {
            return;
        }

        _setIsLoadingChannelCards(true);
        prevChannelsPageCounter.value += 1;
        const channels = availableChannels.value.slice(_prevChannelIndex.value, _startChannelIndex.value - ((prevChannelsPageCounter.value - 1) * AppConstants.PlayerItemsInLine));
        const channelIds = channels.map((channel: Channel) => channel.id).filter((id): id is number => id !== null);

        await Services.Instance.guide.getSegmentFromCache(channelIds, currentTime.value);

        _channelsCards.value.unshift(...channels);
        _setIsLoadingChannelCards(false);
    };

    const clearPageChannelCards = (): void => {
        prevChannelsPageCounter.value = 0;
        nextChannelsPageCounter.value = 0;
        _channelsCards.value = [];
    };

    const startPlayingChannel = async (item: ChannelLiveCardData, redirect: boolean = true): Promise<void> => {
        try {
            if (isGuest.value) {
                showPreviewSignInModal(true);
                return;
            }
    
            const channelId = {"id": item.getId()};
            _currentProgram.value = await Services.Instance.guide.getChannelProgramByTime(Number(item.getId()), TimeUtils.getTime());
            if(_currentProgram.value === null) {
                throw new Error(`${errorChannelId}: ${item.getId()}`);
            }

            _playChannel.value = channelId as unknown as PlayItem;
            _playProgram.value = null;
            if(redirect) {
                void router.push(linkItems.player.routerPath); //TODO: Investigate: Remove the router push to the home page from here 
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage($i18n.t(Strings.PLAYER_TOOLTIP_ERROR_STARTING_CHANNEL).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const startPlayingProgram = (item: PlayItem): void => {
        _playProgram.value = item;
        _playChannel.value = null;
        _currentProgram.value = item;
    };
    const playbackProgress = async ({ id, type, progress }: { id: number; type: ContentType; progress: number }): Promise<void> => {
        try {
            await Services.Instance.domains.mediaRepository.setWatchedProgress(id, type, progress);
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const updateCurrentProgram = async ({ channelId }: { channelId: number }): Promise<void> => {
        try {
            _currentProgram.value = await Services.Instance.guide.getChannelProgramByTime(channelId, TimeUtils.getTime());
            if(_currentProgram.value === null) {
                throw new Error(`${errorChannelId}: ${channelId}`);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            throw error; // Re-throw to let caller handle the failure
        }
    };
    const updateCurrentProgramByTime = async ({ channelId, time }: { channelId: number; time: number }): Promise<void> => {
        try {
            _currentProgram.value = await Services.Instance.guide.getChannelProgramByTime(channelId, time);
            if(_currentProgram.value === null) {
                throw new Error(`${errorChannelId}: ${channelId}`);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            throw error; // Re-throw to let caller handle the failure
        }
    };
    const fetchLiveProgram = async ({ channelId }: { channelId: number }): Promise<void> => {
        try {
            _liveProgram.value = await Services.Instance.guide.getChannelProgramByTime(channelId, TimeUtils.getTime());
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const saveSubtitles = (id: string): void => {
        localStorage.setItem("Subtitles", JSON.stringify({"subtitle": id}));
        setSubtitles();
    };
    const saveAudio = (id: string): void => {
        localStorage.setItem("Audio", JSON.stringify({"audio": id}));
        setAudio();
    };
    const fetchNpasMessage = async (): Promise<void> => {
        try {
            let alerts = null;
            const templates = await Services.Instance.domains.npasRepository.getMessageAlertCheck();
            _alertsIds.value = templates;
            if (templates.length > 0) {
                alerts = await Services.Instance.domains.npasRepository.getAlertRetrieve(templates);
                _alertsRetrieved.value = alerts;
            }
        } catch (error) {
            Logger.captureError(new Error('fetchNpasMessage error', { cause: error as AxiosError | Error }));
        }
    };
    const successfulAllMessagesPlayback = async (): Promise<void> => {
        try {
            if (alertsIds.value) {
                await Services.Instance.domains.npasRepository.setAlertAck(alertsIds.value);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _relatedContent: Ref<OnDemandContent | null> = ref(null);
    const fetchRelatedContent = async (item: PlayItem | null): Promise<void> => {
        try {
            if(!item) {
                return;
            }
            _relatedContent.value = await useFetchRelatedContent(item, AppConstants.PlayerItemsInLine);
        } catch (error) {
            _relatedContent.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getRelatedCards = (): CardData[] | null => {
        if (_relatedContent.value === null) {
            return null;
        }

        if (!_relatedContent.value.items || _relatedContent.value.items.length === 0) {
            return [];
        }

        return _relatedContent.value.items.map((item) => new OnDemandCardData(item));
    };
    const clearLineRelatedCards = (): void => {
        _relatedContent.value = null;
    };


    const setSubtitles = (): void => {
        const itemLocalStorage = JSON.parse(localStorage.getItem("Subtitles")!);
        _subtitles.value = itemLocalStorage ? itemLocalStorage.subtitle : SubtitlesButtonsTypes.OFF;
    };
    const setAudio = (): void => {
        const itemLocalStorage = JSON.parse(localStorage.getItem("Audio")!);
        _audio.value = itemLocalStorage ? itemLocalStorage.audio : '';
    };
    const setPlayerAvailability = (item: boolean): void => {
        _playerAvailability.value = item;
    };
    const setPlayerInitialized = (value: boolean): void => {
        _isPlayerInitialized.value = value;
    };
    const setUpdateProgram = (value: boolean): void => {
        _isUpdateProgram.value = value;
    };
    const setPlayerClosure = (value: boolean): void => {
        _closePlayer.value = value;
    };

    const setPlayFromBeginning = (value: boolean): void => {
        _isPlayBeginning.value = value;
    };
    const setIsShownAlertModal = (value: boolean): void => {
        _isAlertModalVisible.value = value;
    };
    const setBifImage = (value: string | null): void => {
        _bifImage.value = value;
    };

    const _volume: Ref<number> = ref(100);
    const getVolume = computed((): number => _volume.value);
    const setVolume = (value: number): void  => {
        _volume.value = value;
    };

    const _visibleDescription: Ref<string> = ref('off');
    const isVisibleDescription = computed((): boolean => _visibleDescription.value === 'on' ? true : false);
    const setVisibleDescription = (): void  => {
        const itemLocalStorage = JSON.parse(localStorage.getItem("Description")!);
        _visibleDescription.value = itemLocalStorage ? itemLocalStorage.description : 'off';
    };
    const saveVisibleDescription = (show: boolean): void  => {
        const visible = show ? 'on' : 'off';
        localStorage.setItem("Description", JSON.stringify({"description": visible}));
        setVisibleDescription();
    };

    const setToInitialState = (): void => {
        _playProgram.value = null;
        _playChannel.value = null;
        _currentProgram.value = null;
        _subtitles.value = '';
        _audio.value = '';
        _liveProgram.value = null;
        _playerAvailability.value = false;
        _isPlayerInitialized.value = false;
        _isUpdateProgram.value = false;
        _closePlayer.value = false;
        _playingIsChannel.value = false;
        _currentChannel.value = null;
        _isPlayBeginning.value = false;
        _alertsIds.value = [];
        _alertsRetrieved.value = [];
        _isAlertModalVisible.value = false;
        _videoResume.value = false;
        _bifImage.value = null;
        if (_playProgramDebounceTimeout) {
            clearTimeout(_playProgramDebounceTimeout);
        }
    }

    const getIsLoadingEpisodes = computed((): boolean => _isLoadingEpisodesDetailPage.value || _isLoadingEpisodesHomePage.value);
    
    const getEpisodesProperties = computed((): ContentProperties[] => {
        if (_episodesPropertiesHomePage.value.length > 0) {
            return _episodesPropertiesHomePage.value;
        }
        
        if (_episodesPropertiesDetailPage.value.length > 0) {
            return _episodesPropertiesDetailPage.value;
        }

        return [];
    });

    const _episodesCards: Ref<OnDemandItem[]> = ref([]);

    const getEpisodes = computed((): OnDemandItem[] => {
        const data = _episodesHomePage.value ?? _episodesDetailPage.value;
        return data?.items ?? [];
    });

    const _startEpisodesIndex = computed((): number => {
        if (!playProgram.value?.id) {
            return 0;
        }

        const playingEpisodesIndex = getEpisodes.value.findIndex(item => item.id === playProgram.value?.id);
        if (playingEpisodesIndex === -1) {
            return 0;
        }

        const maxStartIndex = getEpisodes.value.length - _VISIBLE_ITEMS_IN_LINE;
        if (playingEpisodesIndex > maxStartIndex) {
            return Math.max(0, maxStartIndex);
        }

        return playingEpisodesIndex;
    });

    const initEpisodesCards = (): void => {
        _episodesCards.value = getEpisodes.value.slice(_startEpisodesIndex.value, _startEpisodesIndex.value + AppConstants.EpisodesInLine);
    };

    const _applyEpisodesProperties = (items: EpisodeCardData[]): void => {
        if (getEpisodesProperties.value.length === 0) {
            return;
        }

        items.forEach((episode: EpisodeCardData) => {
            const id = episode.getPlayItem().id;
            const prop = getEpisodesProperties.value.find((propItem: ContentProperties) => propItem.id === id);
            if (prop) {
                episode.setPosition(prop.streamPosition as number);
            }
        });
    };

    const getEpisodesCards = computed((): CardData[] | null => {
        const items: EpisodeCardData[] = _episodesCards.value.map((item, index) => new EpisodeCardData(item, index));

        _applyEpisodesProperties(items);
        
        return items;
    });
    const loadPreviousEpisodesPage = (): void => {
        const firstAddedIndex = getEpisodes.value.findIndex(item => item.id === _episodesCards.value[0]?.id);
        if (firstAddedIndex === 0) {
            return;
        }

        const newEpisodes = getEpisodes.value.slice(Math.max(firstAddedIndex - AppConstants.EpisodesInLine, 0), firstAddedIndex);
        _episodesCards.value.unshift(...newEpisodes);
    };

    const _fetchNextPageFromStore = async (): Promise<void> => {
        if (_episodesHomePage.value) {
            await fetchNextPageOfEpisodesHomePage();
        } else {
            await fetchNextPageOfEpisodesDetailShowPage();
        }
    };

    const pushNewEpisodes = (startIndex: number): void => {
        const newEpisodes = getEpisodes.value.slice(startIndex, startIndex + AppConstants.EpisodesInLine);
        _episodesCards.value.push(...newEpisodes);
    };

    const loadNextEpisodesPage = async (): Promise<void> => {
        const lastAddedIndex = getEpisodes.value.findIndex(item => item.id === _episodesCards.value[_episodesCards.value.length - 1]?.id);

        if (lastAddedIndex < getEpisodes.value.length - 1) {
            pushNewEpisodes(lastAddedIndex + 1);
            return;
        }

        if (lastAddedIndex >= getEpisodes.value.length - 1) {
            await _fetchNextPageFromStore();
            
            const updatedLastAddedIndex = getEpisodes.value.findIndex(item => item.id === _episodesCards.value[_episodesCards.value.length - 1]?.id);
            pushNewEpisodes(updatedLastAddedIndex + 1);
        }
    };

    // call once initEpisodesCards when getEpisodes.value changes for initialization from home page
    watch(getEpisodes, (newEpisodes, oldEpisodes) => {
        if (!oldEpisodes?.length && newEpisodes.length > 0) {
            void nextTick(() => {
                initEpisodesCards();
            });
        }
    }, { immediate: true });

    // watch playProgram and loadNextEpisodesPage when playProgram is the last item in _episodesCards
    // Debounced watcher: avoid rapid consecutive API calls when _playProgram changes quickly
    let _playProgramDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
    watch(_playProgram, (newProgram) => {
        if (!newProgram?.id) {
            return;
        }

        if (_playProgramDebounceTimeout) {
            clearTimeout(_playProgramDebounceTimeout);
        }

        _playProgramDebounceTimeout = setTimeout(() => {
            const isLastInEpisodesCards = _episodesCards.value.length > 0 &&
                newProgram.id === _episodesCards.value[_episodesCards.value.length - 1].id;
            if (isLastInEpisodesCards) {
                void loadNextEpisodesPage();
            }
        }, 100);
    });
    return {
        getVolume,
        isVisibleDescription,
        getEpisodes,
        playProgram,
        playChannel,
        currentProgram,
        subtitles,
        audio,
        liveProgram,
        playerAvailability,
        isPlayerInitialized,
        isUpdateProgram,
        isPlayerClosure,
        playingIsChannel,
        getCurrentChannel,
        isPlayBeginning,
        alertsIds,
        alertsRetrieved,
        isAlertModalVisible,
        videoResume,
        bifImage,
        getIsLoadingEpisodes,
        getEpisodesCards,
        getChannelsCards,
        loadNextChannelPage,
        clearPageChannelCards,
        loadPreviousChannelPage,
        initChannelCards,
        startPlayingChannel,
        startPlayingProgram,
        playbackProgress,
        updateCurrentProgram,
        updateCurrentProgramByTime,
        fetchLiveProgram,
        saveSubtitles,
        saveAudio,
        fetchNpasMessage,
        successfulAllMessagesPlayback,
        setSubtitles,
        setAudio,
        setCurrentChannel,
        setPlayerAvailability,
        setPlayerInitialized,
        setUpdateProgram,
        setPlayerClosure,
        setPlayingIsChannel,
        setPlayFromBeginning,
        setIsShownAlertModal,
        setVideoResume,
        setBifImage,
        setToInitialState,
        fetchRelatedContent,
        getRelatedCards,
        clearLineRelatedCards,
        saveVisibleDescription,
        setVisibleDescription,
        setVolume,
        initEpisodesCards,
        loadPreviousEpisodesPage,
        loadNextEpisodesPage,
        getIdDetailPage,
        getIsLoadingChannelCards
    }
});